import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Fuel } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { act } from "@/lib/hooks";
import { Field, PageHeader, Panel } from "@/components/common";
import { ClientVehiclePicker } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function ReceptionNew() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [sel, setSel] = useState<any>({ client: null, vehicle: null });
  const [items, setItems] = useState<string[]>([]);
  const [f, setF] = useState<any>({ mileage: "", fuel_level: 4, checklist: {}, damages: "", customer_request: "", keys_count: 1 });

  useEffect(() => {
    api.get("/receptions/checklist").then((r) => setItems(r.data));
    const vid = params.get("vehicle");
    if (vid) {
      api.get(`/vehicles/${vid}`).then((r) => {
        setSel({ client: r.data.client, vehicle: r.data });
        setF((x: any) => ({ ...x, mileage: r.data.mileage || "" }));
      });
    }
    const aid = params.get("appointment");
    if (aid) setF((x: any) => ({ ...x, appointment_id: aid }));
  }, [params]);

  const toggle = (k: string) => setF({ ...f, checklist: { ...f.checklist, [k]: !f.checklist[k] } });
  const submit = async () => {
    if (!sel.vehicle) return toast.error("Choisissez le véhicule");
    if (f.mileage === "") return toast.error("Le kilométrage est obligatoire");
    const checklist = Object.fromEntries(items.map((k) => [k, !!f.checklist[k]]));
    const r = await act(() => api.post("/receptions", { ...f, checklist, vehicle_id: sel.vehicle.id, mileage: Number(f.mileage) }), "Véhicule réceptionné");
    if (r) nav(`/receptions/${r.data.id}`);
  };

  return (
    <div className="max-w-5xl">
      <PageHeader eyebrow="Bon d'entrée" title="Réception du véhicule" />
      <div className="space-y-6">
        <Panel title="Client et véhicule"><ClientVehiclePicker value={sel} onChange={setSel} /></Panel>
        <Panel title="État à l'arrivée">
          <div className="grid gap-6 md:grid-cols-2">
            <Field label="Kilométrage (km)"><Input type="number" inputMode="numeric" value={f.mileage} onChange={(e) => setF({ ...f, mileage: e.target.value })} className="h-14 font-mono-data text-xl" data-testid="reception-mileage-input" /></Field>
            <Field label="Clés remises"><Input type="number" value={f.keys_count} onChange={(e) => setF({ ...f, keys_count: Number(e.target.value) })} className="h-14 text-xl" data-testid="reception-keys-input" /></Field>
          </div>
          <div className="mt-6">
            <div className="eyebrow mb-2 flex items-center gap-2"><Fuel className="h-3.5 w-3.5" />Niveau de carburant : {f.fuel_level}/8</div>
            <div className="grid grid-cols-9 gap-1" data-testid="reception-fuel-gauge">
              {Array.from({ length: 9 }, (_, i) => (
                <button key={i} onClick={() => setF({ ...f, fuel_level: i })} data-testid={`fuel-level-${i}`}
                  className={`h-12 rounded-sm border text-sm font-semibold transition-colors ${i <= f.fuel_level ? "border-amber-500/60 bg-amber-500/20 text-amber-200 [.light_&]:text-amber-800" : "border-border hover:bg-accent"}`}>
                  {i === 0 ? "V" : i === 8 ? "P" : i}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6">
            <div className="eyebrow mb-2">Check-list</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((k) => (
                <button key={k} onClick={() => toggle(k)} data-testid={`checklist-${k}`}
                  className={`flex h-14 items-center gap-3 rounded-sm border px-4 text-left text-sm transition-colors ${f.checklist[k] ? "border-emerald-500/60 bg-emerald-500/10" : "border-border hover:bg-accent"}`}>
                  <span className={`flex h-6 w-6 items-center justify-center rounded-sm border ${f.checklist[k] ? "border-emerald-400 bg-emerald-500 text-white" : "border-border"}`}>{f.checklist[k] && <Check className="h-4 w-4" />}</span>
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <Field label="Demande du client"><Textarea rows={4} value={f.customer_request} onChange={(e) => setF({ ...f, customer_request: e.target.value })} placeholder="Vidange + bruit au freinage" data-testid="reception-request-input" /></Field>
            <Field label="Dégâts / remarques carrosserie"><Textarea rows={4} value={f.damages} onChange={(e) => setF({ ...f, damages: e.target.value })} placeholder="Rayure porte AR gauche…" data-testid="reception-damages-input" /></Field>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Les photos se prennent à l'étape suivante, sur la fiche de réception.</p>
        </Panel>
        <Button className="h-14 w-full text-base font-bold uppercase tracking-wider" onClick={submit} data-testid="reception-submit-btn">Valider la réception</Button>
      </div>
    </div>
  );
}
